// AddTeamsModal.jsx

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createTeamMember,
  fetchTeamMembers,
  updateTeamMember,
  deleteTeamMember,
} from '../redux/slices/teamSlice';

import '../modalstyles/AddTeamsModal.css';

/* ensures URLs open correctly even if stored without protocol */
const ensureUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `https://${url}`;
};
const getImageUrl = (path) => {
  if (!path) return '';

  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  const cleanPath = path.replace(/^images\//, '');

  return `https://payyug.in/${cleanPath}`;
};

const AddTeamsModal = ({ onClose }) => {
  const dispatch = useDispatch();

  const { list: teams, loading } = useSelector(
    (state) => state.team
  );

  const superAdminId =
    localStorage.getItem('adminuserId') || '12';

  const initialState = {
    name: '',
    designation: '',
    file: null,
    linkedin: '',
    twitter: '',
    instagram: '',
    facebook: '',
  };

  const [team, setTeam] = useState(initialState);

  const [editingId, setEditingId] = useState(null);

  const [preview, setPreview] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');

  const [currentPage, setCurrentPage] = useState(1);

  const [itemsPerPage, setItemsPerPage] = useState(5);

  /* ---- NEW: edit dialog state ---- */
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editDialogData, setEditDialogData] = useState(initialState);
  const [editDialogId, setEditDialogId] = useState(null);
  const [editDialogPreview, setEditDialogPreview] = useState(null);

  useEffect(() => {
    dispatch(fetchTeamMembers(superAdminId));
  }, [dispatch, superAdminId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTeam((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTeam((prev) => ({ ...prev, file }));
      setPreview(URL.createObjectURL(file));
    }
  };

  const clearForm = () => {
    setTeam(initialState);
    setEditingId(null);
    setPreview(null);
  };

  const handleSave = async () => {
    if (!team.name || !team.designation) {
      alert('Name and designation are required');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('name', team.name);
      formData.append('designation', team.designation);
      formData.append('linkedin', team.linkedin);
      formData.append('twitter', team.twitter);
      formData.append('instagram', team.instagram);
      formData.append('facebook', team.facebook);
      formData.append('superAdminId', superAdminId);
      if (team.file) formData.append('file', team.file);

      if (editingId) {
        await dispatch(updateTeamMember({ id: editingId, formData })).unwrap();
      } else {
        await dispatch(createTeamMember(formData)).unwrap();
      }
      clearForm();
      dispatch(fetchTeamMembers(superAdminId));
    } catch (error) {
      console.error(error);
    }
  };

  /* ---- UPDATED: handleEdit opens dialog instead of scrolling ---- */
  const handleEdit = (member) => {
    setEditDialogData({
      name: member?.name || '',
      designation: member?.designation || '',
      file: null,
      linkedin: member?.linkedin || '',
      twitter: member?.twitter || '',
      instagram: member?.instagram || '',
      facebook: member?.facebook || '',
    });
    setEditDialogId(member?._id || member?.id);
    setEditDialogPreview(
  member?.photo ? getImageUrl(member.photo) : null
);
    setEditDialogOpen(true);
  };

  const handleEditDialogChange = (e) => {
    const { name, value } = e.target;
    setEditDialogData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditDialogFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditDialogData((prev) => ({ ...prev, file }));
      setEditDialogPreview(URL.createObjectURL(file));
    }
  };

  const handleEditDialogSubmit = async () => {
    if (!editDialogData.name || !editDialogData.designation) {
      alert('Name and designation are required');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('name', editDialogData.name);
      formData.append('designation', editDialogData.designation);
      formData.append('linkedin', editDialogData.linkedin);
      formData.append('twitter', editDialogData.twitter);
      formData.append('instagram', editDialogData.instagram);
      formData.append('facebook', editDialogData.facebook);
      formData.append('superAdminId', superAdminId);
      if (editDialogData.file) formData.append('file', editDialogData.file);

      await dispatch(updateTeamMember({ id: editDialogId, formData })).unwrap();
      setEditDialogOpen(false);
      dispatch(fetchTeamMembers(superAdminId));
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this member?');
    if (!confirmDelete) return;
    await dispatch(deleteTeamMember(id));
    dispatch(fetchTeamMembers(superAdminId));
  };

  const filteredTeams = Array.isArray(teams)
    ? teams.filter((member) => {
        const name = member?.name?.toLowerCase() || '';
        const designation = member?.designation?.toLowerCase() || '';
        return (
          name.includes(searchTerm.toLowerCase()) ||
          designation.includes(searchTerm.toLowerCase())
        );
      })
    : [];

  const totalPages = Math.ceil(filteredTeams.length / itemsPerPage);

  const paginatedTeams = filteredTeams.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="team-modal-backdrop">
      <div className="team-modal-container">

        {/* HEADER */}
        <div className="team-modal-header">
          <div>
            <h2>Team Management</h2>
            <p>Add, update and manage your team members</p>
          </div>
          {/* FIX: flex-shrink:0 keeps close button in place */}
          <button className="close-btn" onClick={onClose}>
            &times;
          </button>
        </div>

        {/* BODY */}
        <div className="team-modal-body">

          {/* FORM CARD */}
          <div className="form-card">
            <div className="form-grid">
              <input
                type="text"
                name="name"
                placeholder="Enter Name"
                value={team.name}
                onChange={handleChange}
              />
              <input
                type="text"
                name="designation"
                placeholder="Enter Designation"
                value={team.designation}
                onChange={handleChange}
              />
              <input
                type="text"
                name="linkedin"
                placeholder="LinkedIn URL"
                value={team.linkedin}
                onChange={handleChange}
              />
              <input
                type="text"
                name="twitter"
                placeholder="Twitter URL"
                value={team.twitter}
                onChange={handleChange}
              />
              <input
                type="text"
                name="instagram"
                placeholder="Instagram URL"
                value={team.instagram}
                onChange={handleChange}
              />
              <input
                type="text"
                name="facebook"
                placeholder="Facebook URL"
                value={team.facebook}
                onChange={handleChange}
              />
            </div>

            <div className="upload-section">
              <input type="file" accept="image/*" onChange={handleFileChange} />
              {preview && (
                <img src={preview} alt="preview" className="preview-image" />
              )}
            </div>

            <div className="form-buttons">
              <button className="save-btn" onClick={handleSave} disabled={loading}>
                {loading ? 'Processing...' : editingId ? 'Update Member' : 'Add Member'}
              </button>
              <button className="clear-btn" onClick={clearForm}>
                Clear
              </button>
            </div>
          </div>

          {/* SEARCH + CONTROLS */}
          <div className="table-topbar">
            <input
              type="text"
              className="search-box"
              placeholder="Search by name/designation..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={5}>5 Rows</option>
              <option value={10}>10 Rows</option>
              <option value={15}>15 Rows</option>
            </select>
          </div>

          {/* TABLE */}
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>SN</th>
                  <th>Photo</th>
                  <th>Name</th>
                  <th>Designation</th>
                  <th>LinkedIn</th>
                  <th>Twitter</th>
                  <th>Instagram</th>
                  <th>Facebook</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9">Loading...</td>
                  </tr>
                ) : paginatedTeams.length > 0 ? (
                  paginatedTeams.map((member, index) => (
                    <tr key={member?._id || member?.id}>
                      <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                      <td>
                        {member?.photo ? (
                          <img
  src={getImageUrl(member.photo)}
  alt="team"
  className="table-image"
/>
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td>{member?.name || 'N/A'}</td>
                      <td>{member?.designation || 'N/A'}</td>

                      {/* FIX: ensureUrl() makes links open correctly */}
                      <td>
                        {member?.linkedin ? (
                          <a
                            href={ensureUrl(member.linkedin)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View
                          </a>
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td>
                        {member?.twitter ? (
                          <a
                            href={ensureUrl(member.twitter)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View
                          </a>
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td>
                        {member?.instagram ? (
                          <a
                            href={ensureUrl(member.instagram)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View
                          </a>
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td>
                        {member?.facebook ? (
                          <a
                            href={ensureUrl(member.facebook)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View
                          </a>
                        ) : (
                          'N/A'
                        )}
                      </td>

                      <td>
                        <div className="action-buttons">
                          <button
                            className="edit-btn"
                            onClick={() => handleEdit(member)}
                          >
                            Edit
                          </button>
                          <button
                            className="delete-btn"
                            onClick={() =>
                              handleDelete(member?._id || member?.id)
                            }
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9">No Team Members Found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          <div className="pagination">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Prev
            </button>
            <span>
              Page {currentPage} of {totalPages || 1}
            </span>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* ---- NEW: EDIT DIALOG ---- */}
      {editDialogOpen && (
        <div className="edit-dialog-backdrop" onClick={() => setEditDialogOpen(false)}>
          <div className="edit-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="edit-dialog-header">
              <h3>Update Team Member</h3>
              <button
                className="edit-dialog-close"
                onClick={() => setEditDialogOpen(false)}
              >
                &times;
              </button>
            </div>

            <div className="edit-dialog-body">
              <div className="edit-dialog-grid">
                <div className="edit-field">
                  <label>Name</label>
                  <input
                    type="text"
                    name="name"
                    placeholder="Enter Name"
                    value={editDialogData.name}
                    onChange={handleEditDialogChange}
                  />
                </div>
                <div className="edit-field">
                  <label>Designation</label>
                  <input
                    type="text"
                    name="designation"
                    placeholder="Enter Designation"
                    value={editDialogData.designation}
                    onChange={handleEditDialogChange}
                  />
                </div>
                <div className="edit-field">
                  <label>LinkedIn URL</label>
                  <input
                    type="text"
                    name="linkedin"
                    placeholder="LinkedIn URL"
                    value={editDialogData.linkedin}
                    onChange={handleEditDialogChange}
                  />
                </div>
                <div className="edit-field">
                  <label>Twitter URL</label>
                  <input
                    type="text"
                    name="twitter"
                    placeholder="Twitter URL"
                    value={editDialogData.twitter}
                    onChange={handleEditDialogChange}
                  />
                </div>
                <div className="edit-field">
                  <label>Instagram URL</label>
                  <input
                    type="text"
                    name="instagram"
                    placeholder="Instagram URL"
                    value={editDialogData.instagram}
                    onChange={handleEditDialogChange}
                  />
                </div>
                <div className="edit-field">
                  <label>Facebook URL</label>
                  <input
                    type="text"
                    name="facebook"
                    placeholder="Facebook URL"
                    value={editDialogData.facebook}
                    onChange={handleEditDialogChange}
                  />
                </div>
              </div>

              <div className="edit-dialog-upload">
                <label>Profile Photo</label>
                <input type="file" accept="image/*" onChange={handleEditDialogFile} />
                {editDialogPreview && (
                  <img
                    src={editDialogPreview}
                    alt="preview"
                    className="preview-image"
                  />
                )}
              </div>
            </div>

            <div className="edit-dialog-footer">
              <button
                className="save-btn"
                onClick={handleEditDialogSubmit}
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Save Changes'}
              </button>
              <button
                className="clear-btn"
                onClick={() => setEditDialogOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddTeamsModal;